/* =============================================================================
   LOOMA — collection / PLP controllers (spec §4.3)
   <looma-collection>    AJAX facets + sort + history + skeleton infinite scroll
   <looma-filter-drawer> slide-over filter drawer (focus-trap, Esc, groups)
   No-JS safe: facet <form method=get> + chip/clear <a> + numbered pager all
   work without this script.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function withSection(url, id) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + id;
  }

  /* ---- <looma-collection> ------------------------------------------------ */
  if (!customElements.get('looma-collection')) {
    customElements.define('looma-collection', class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this._loading = false;
        var self = this;
        // Delegated click bound ONCE — the host is never replaced, only its
        // children are, so closest() keeps working across AJAX renders.
        this.addEventListener('click', function (e) {
          var open = e.target.closest('[data-fdrawer-open]');
          if (open) {
            var d = self.querySelector('looma-filter-drawer');
            if (d && d.open) { e.preventDefault(); d.open(); }
            return;
          }
          var link = e.target.closest('[data-facet-link], [data-facet-clear]');
          if (link && link.getAttribute('href')) {
            e.preventDefault();
            self.render(link.getAttribute('href'));
          }
        });
        this._bindForm();
        this._observe();
      }

      _bindForm() {
        var self = this;
        var form = this.querySelector('[data-facet-form]');
        if (form) {
          form.addEventListener('submit', function (e) {
            e.preventDefault();
            self.render(self._formUrl(form));
            var d = self.querySelector('looma-filter-drawer');
            if (d && d.close) d.close();
          });
        }
        var sort = this.querySelector('[data-facet-sort]');
        if (sort) sort.addEventListener('change', function () {
          if (form) self.render(self._formUrl(form));
        });
      }

      _formUrl(form) {
        var base = location.pathname;
        var params = new URLSearchParams();
        new FormData(form).forEach(function (v, k) {
          if (v !== '' && v != null) params.append(k, v);
        });
        var qs = params.toString();
        return qs ? base + '?' + qs : base;
      }

      render(url, skipPush) {
        if (this._loading) return;
        this._loading = true;
        this.classList.add('is-loading');
        var self = this;
        fetch(withSection(url, this.sectionId), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            ['#ProductGridContainer', '[data-facet-form]'].forEach(function (sel) {
              var fresh = doc.querySelector(sel), cur = self.querySelector(sel);
              if (fresh && cur) cur.replaceWith(fresh);
            });
            if (!skipPush) history.pushState({ looma: true }, '', url);
            self._bindForm();
            self._observe();
            window.Looma && window.Looma.emit && window.Looma.emit('looma:collection:updated', {});
            var top = self.getBoundingClientRect().top + window.pageYOffset - 80;
            if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
          })
          .catch(function () { location.href = url; })
          .then(function () { self._loading = false; self.classList.remove('is-loading'); });
      }

      _observe() {
        if (this._io) this._io.disconnect();
        var sentinel = this.querySelector('[data-sentinel]');
        if (!sentinel || !('IntersectionObserver' in window)) return;
        var pager = this.querySelector('[data-pager]');
        if (pager) pager.hidden = true;
        var self = this;
        this._io = new IntersectionObserver(function (en) {
          if (en[0].isIntersecting) self._loadMore(sentinel);
        }, { rootMargin: '600px 0px' });
        this._io.observe(sentinel);
      }

      _loadMore(sentinel) {
        if (this._loading) return;
        var next = sentinel.dataset.nextUrl;
        if (!next) return;
        this._loading = true;
        var grid = this.querySelector('[data-grid]');
        var self = this;
        var sk = document.createElement('div');
        sk.className = 'sk-batch';
        if (grid) {
          for (var i = 0; i < 4; i++) {
            var li = document.createElement('li');
            li.setAttribute('aria-hidden', 'true');
            li.innerHTML = '<div class="skeleton"><div class="skeleton__img"></div>' +
              '<div class="skeleton__b"><div class="skeleton__line"></div>' +
              '<div class="skeleton__line skeleton__line--sm"></div></div></div>';
            li.dataset.sk = '1';
            grid.appendChild(li);
          }
        }
        fetch(withSection(next, this.sectionId), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            if (grid) {
              grid.querySelectorAll('[data-sk]').forEach(function (n) { n.remove(); });
              var items = doc.querySelectorAll('[data-grid] > li');
              items.forEach(function (n) { grid.appendChild(n); });
            }
            var freshSentinel = doc.querySelector('[data-sentinel]');
            if (freshSentinel && freshSentinel.dataset.nextUrl) {
              sentinel.dataset.nextUrl = freshSentinel.dataset.nextUrl;
            } else {
              sentinel.removeAttribute('data-next-url');
              if (self._io) self._io.disconnect();
            }
            window.Looma && window.Looma.emit && window.Looma.emit('looma:collection:appended', {});
          })
          .catch(function () {
            if (grid) grid.querySelectorAll('[data-sk]').forEach(function (n) { n.remove(); });
            var p = self.querySelector('[data-pager]');
            if (p) p.hidden = false;
          })
          .then(function () { self._loading = false; });
      }
    });
  }

  /* ---- <looma-filter-drawer> -------------------------------------------- */
  if (!customElements.get('looma-filter-drawer')) {
    customElements.define('looma-filter-drawer', class extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.fdrawer');
        this._opener = null;
        this._onKey = this._onKey.bind(this);
        var self = this;
        this.addEventListener('click', function (e) {
          if (e.target.closest('[data-fdrawer-close]')) { e.preventDefault(); self.close(); return; }
          var g = e.target.closest('[data-fgroup]');
          if (g) {
            var open = g.getAttribute('aria-expanded') !== 'false';
            g.setAttribute('aria-expanded', open ? 'false' : 'true');
          }
        });
      }
      open() {
        if (this.classList.contains('is-open')) return;
        this._opener = document.activeElement;
        this.classList.add('is-open');
        document.documentElement.classList.add('no-scroll');
        document.addEventListener('keydown', this._onKey);
        var f = this.querySelector('[data-fdrawer-close]') || this.panel;
        if (f) f.focus();
      }
      close() {
        if (!this.classList.contains('is-open')) return;
        this.classList.remove('is-open');
        document.documentElement.classList.remove('no-scroll');
        document.removeEventListener('keydown', this._onKey);
        if (this._opener && this._opener.focus) this._opener.focus();
      }
      _onKey(e) {
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key !== 'Tab' || !this.panel) return;
        var nodes = [].slice.call(this.panel.querySelectorAll(FOCUSABLE))
          .filter(function (n) { return n.offsetParent !== null; });
        if (!nodes.length) return;
        var first = nodes[0], last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* keep AJAX history in sync with back/forward */
  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.looma) {
      var c = document.querySelector('looma-collection');
      if (c) c.render(location.pathname + location.search, true);
    }
  });
})();
